import type {
  WritingAssistContext,
  WritingAssistUsage,
} from 'api/Api';
import type { WritingAssistVariant } from './WritingAssist.types';

export const COMPOSE_COPY = {
  polish: 'Polish with AI',
  saveAsIs: 'Save as-is',
  contactInfo: "Heads up: contact info (phone, email, links) can't appear here — it's removed when you save.",
  loadingRead: 'Reading your draft…',
  loadingPrepare: 'Preparing your versions…',
  loadingSave: 'Giving it a quick once-over…',
  minWords: (min: number) => `Tell us a little more — at least ${min} words to get going.`,
  exhausted: "You've used today's AI polishes — they refresh tomorrow. You can still save your own writing anytime.",
} as const;

export const REVIEW_COPY = {
  title: 'Choose your version',
  subtitle: 'Pick one to continue — you can still edit it.',
  edit: '✎ Edit this version',
  noSelection: "Pick the version you'd like to save.",
  back: 'Back',
  save: 'Save',
} as const;

export const FAILURE_COPY = {
  burst: 'A few too many requests at once — give it a moment and try again.',
  writer: "We couldn't prepare suggestions just now. Your text is safe — try again in a minute, or save as-is.",
} as const;

export const CONTACT_INFO_COPY = COMPOSE_COPY.contactInfo;

export interface ContextCopyInput {
  context: WritingAssistContext;
  variant?: WritingAssistVariant;
  firstName?: string;
  practiceName?: string;
}

export interface WritingAssistContextCopy {
  title: string;
  subtitle: string;
  placeholder: string;
  minWords: number;
  skipLabel?: string;
}

const COVER_LETTER_PLACEHOLDER = 'Introduce yourself and highlight your experience…';

export function contextCopy({
  context,
  variant = 'profile',
  firstName = '',
  practiceName = '',
}: ContextCopyInput): WritingAssistContextCopy {
  if (context === 'aboutMe') {
    return {
      title: 'About You',
      subtitle: `Hey ${firstName}! Tell offices how you shine.`,
      placeholder: 'What do you love about dentistry? What should offices know about working with you?',
      minWords: 40,
      skipLabel: variant === 'signup' ? 'Skip for now — you can add this later' : undefined,
    };
  }
  if (context === 'baseCoverLetter') {
    return {
      title: 'Base Cover Letter',
      subtitle: 'Your starting point for every application — make it count.',
      placeholder: COVER_LETTER_PLACEHOLDER,
      minWords: 80,
    };
  }
  if (context === 'applicationCoverLetter') {
    return {
      title: 'Cover Letter',
      subtitle: `This goes with your application to ${practiceName}.`,
      placeholder: COVER_LETTER_PLACEHOLDER,
      minWords: 80,
    };
  }
  return {
    title: 'Job Description',
    subtitle: 'Step 2 of 2 — tell candidates about the role and your office.',
    placeholder: 'The role, the schedule, your team, what makes your office a great place to work…',
    minWords: 60,
  };
}

export function countWords(input: string): number {
  const trimmed = input.trim();
  return trimmed.length === 0 ? 0 : trimmed.split(/\s+/).length;
}

type ContactType = 'email' | 'url' | 'phone';

export interface ContactDetection {
  detected: boolean;
  categories: ContactType[];
  original: string;
}

const EMAIL_RE = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;
const URL_RE = /(?:https?:\/\/|www\.)[^\s<>"']+/i;
const BARE_DOMAIN_RE = /\b[a-z0-9-]+\.(?:com|ca|net|org|io|co|us|info|biz)\b/i;
const PHONE_RE = /(?:^|[^\d-])(?:\+?1[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}(?=$|[^\d-])/;

export function detectContactInfo(input: string): ContactDetection {
  const categories: ContactType[] = [];
  const hasEmail = EMAIL_RE.test(input);
  if (hasEmail) categories.push('email');
  // The server removes emails before its URL pass, so an email domain is not double-counted as a URL.
  const withoutEmails = hasEmail ? input.replace(new RegExp(EMAIL_RE.source, 'gi'), '') : input;
  if (URL_RE.test(withoutEmails) || BARE_DOMAIN_RE.test(withoutEmails)) categories.push('url');
  if (PHONE_RE.test(input)) categories.push('phone');
  return { detected: categories.length > 0, categories, original: input };
}

export function usageCopy(usage: WritingAssistUsage): string {
  const remaining = Math.max(0, usage.max - usage.used);
  if (remaining === 0) return COMPOSE_COPY.exhausted;
  return `${remaining} of ${usage.max} AI polishes left today`;
}
